"use client"
import { Link } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { Smartphone, Wifi, Zap, Shield, Users, TrendingUp } from "lucide-react"
import Card from "../components/Card"
import Button from "../components/Button"
import '../index.css'

const LandingPage = () => {
  const { user } = useAuth()

  const networks = [
    { name: "MTN", logo: "🟡", color: "bg-yellow-500" },
    { name: "Airtel", logo: "🔴", color: "bg-red-500" },
    { name: "Glo", logo: "🟢", color: "bg-green-500" },
    { name: "9mobile", logo: "🟢", color: "bg-green-600" },
  ]

  const features = [
    {
      icon: <Smartphone className="h-8 w-8 text-blue-600" />,
      title: "Instant Data Purchase",
      description: "Buy data for all Nigerian networks instantly with competitive prices",
    },
    {
      icon: <Wifi className="h-8 w-8 text-blue-600" />,
      title: "Airtime Top-up",
      description: "Quick and easy airtime recharge for yourself or others",
    },
    {
      icon: <Zap className="h-8 w-8 text-blue-600" />,
      title: "Bill Payments",
      description: "Pay for cable TV, electricity bills and other utilities",
    },
    {
      icon: <Shield className="h-8 w-8 text-blue-600" />,
      title: "Secure Transactions",
      description: "All transactions are encrypted and secure",
    },
    {
      icon: <Users className="h-8 w-8 text-blue-600" />,
      title: "Reseller Program",
      description: "Join our reseller program and earn commissions",
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-blue-600" />,
      title: "Best Prices",
      description: "Competitive pricing with regular discounts and offers",
    },
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-black via-gray-900 to-blue-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Nigeria's #1 <span className="text-red-500">Data</span> & <span className="text-blue-500">Airtime</span>{" "}
              Platform
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-300">
              Buy data, airtime, and pay bills instantly for all Nigerian networks
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user ? (
                <Link to="/dashboard">
                  <Button size="lg" className="w-full sm:w-auto">
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/register">
                    <Button size="lg" className="w-full sm:w-auto">
                      Get Started
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full sm:w-auto text-white border-white hover:bg-white hover:text-black"
                    >
                      Login
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Networks Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Supported Networks</h2>
            <p className="text-lg text-gray-600">We support all major Nigerian mobile networks</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {networks.map((network) => (
              <Card key={network.name} className="text-center hover:shadow-lg transition-shadow">
                <div
                  className={`w-16 h-16 ${network.color} rounded-full flex items-center justify-center mx-auto mb-4`}
                >
                  <span className="text-2xl">{network.logo}</span>
                </div>
                <h3 className="text-xl font-semibold">{network.name}</h3>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose DataHub?</h2>
            <p className="text-lg text-gray-600">Experience the best in digital services</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center">
                <div className="flex justify-center mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8">Join thousands of satisfied customers today</p>
          {!user && (
            <Link to="/register">
              <Button variant="outline" size="lg" className="bg-white text-red-600 hover:bg-gray-100">
                Create Account Now
              </Button>
            </Link>
          )}
        </div>
      </section>
    </div>
  )
}

export default LandingPage
